/**
 * The only code in the module that knows a storage medium exists (ADR-001).
 *
 * One namespaced key holds the whole collection, so the module can neither
 * collide with nor read another module's state in the shared origin (threat
 * model, asset 4). Swapping this file for an HTTP adapter is the whole API
 * migration: the port stays, the providers keep taking a repository prop, no UI
 * file changes.
 *
 * Every method is async even though the store is synchronous underneath —
 * that is the point of the port.
 *
 * WHAT IS STORED
 * --------------
 * The published envelope from `contract/`, not a bare array. The value in this
 * key is the same document another application would be handed, which means the
 * format people actually have on disk is the one the schema describes — rather
 * than a private shape that has to be translated, and can therefore drift.
 * Bare arrays written by earlier builds are still read (see `contract/decode`);
 * only envelopes are written.
 */

import { encodeCollection, decodeCollection } from '../contract';
import { CardListQuery, ProductCard, ProductCardsRepository } from '../types';
import {
  listingRowFromCard,
  selectListingPage,
  selectMatchingCards,
  sortCardsForListing,
} from '../selectors';
import { createSeedCards } from './seed';
import { createDefaultCard } from '../state';

export const PRODUCT_CARDS_STORAGE_KEY = 'gms.productCards.v1';

/**
 * Where an unreadable payload is moved before anything overwrites it.
 *
 * The bug this closes: `readAll` used to answer `[]` for a corrupt value, and
 * the very next `save` wrote a one-card collection over it. A user with twenty
 * cards and one bad byte lost nineteen of them, silently, by opening the page
 * and typing. Now the original bytes are copied here first, so the loss is
 * recoverable by hand and there is evidence of what happened.
 */
export const PRODUCT_CARDS_QUARANTINE_KEY = 'gms.productCards.quarantine';

const nowIso = (): string => new Date().toISOString();

/**
 * Storage can be absent (server render) or refuse to answer (private mode,
 * quota, a blocked origin). None of those is worth crashing a page over, so
 * both accessors degrade to in-memory behaviour.
 */
const getStore = (): Storage | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return window.localStorage;
  } catch {
    return null;
  }
};

const readRaw = (): string | null => {
  const store = getStore();
  if (!store) {
    return null;
  }
  try {
    return store.getItem(PRODUCT_CARDS_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeRaw = (cards: ProductCard[]): void => {
  const store = getStore();
  if (!store) {
    return;
  }
  try {
    store.setItem(
      PRODUCT_CARDS_STORAGE_KEY,
      JSON.stringify(encodeCollection(cards, nowIso()))
    );
  } catch {
    // A full or locked store must not take the editor down; the in-memory
    // document stays correct and the next write retries.
  }
};

/** Copies bytes we could not read out of the way, once, before anything writes. */
const quarantine = (raw: string): void => {
  const store = getStore();
  if (!store) {
    return;
  }
  try {
    // Never overwrite an existing quarantine: the first corruption is the one
    // that still has the user's data in it. A second failure writing over it
    // would destroy exactly what this key exists to preserve.
    if (store.getItem(PRODUCT_CARDS_QUARANTINE_KEY) !== null) {
      return;
    }
    store.setItem(
      PRODUCT_CARDS_QUARANTINE_KEY,
      JSON.stringify({ quarantinedAt: nowIso(), raw })
    );
  } catch {
    // Quota, most likely — the payload is large enough to have broken the read.
    // Nothing further to try; the read below still refuses to destroy anything.
  }
};

let idCounter = 0;

const nextCardId = (): string => {
  idCounter += 1;
  return `card-${Date.now().toString(36)}-${idCounter.toString(36)}`;
};

/**
 * A browser-storage adapter for the Product Cards repository port.
 *
 * On a first visit — no key at all — the seed cards are written once. An
 * explicitly empty collection is respected, so deleting every card does not
 * resurrect the seed on the next read.
 */
export const createLocalProductCardsRepository = (): ProductCardsRepository => {
  const readAll = (): ProductCard[] => {
    const raw = readRaw();
    if (raw === null) {
      const seeded = createSeedCards();
      writeRaw(seeded);
      return seeded;
    }
    if (raw === '') {
      return [];
    }

    const { cards, rejected } = decodeCollection(raw, nowIso());

    // Something in the payload could not be read. Whether that is the whole
    // document or one entry, the bytes go to the quarantine key before any
    // caller gets the chance to save over them.
    if (rejected.length > 0) {
      quarantine(raw);
    }

    return cards;
  };

  return {
    // The same query semantics the server answers, from the module's own pure
    // selectors — one definition of "searched, sorted, one page".
    list: async (query: CardListQuery) => {
      const matching = selectMatchingCards(readAll(), query.search);
      const sorted = sortCardsForListing(matching, {
        columnId: query.sortBy,
        direction: query.sortDir,
      });
      return {
        rows: selectListingPage(sorted, query.page, query.limit).map(listingRowFromCard),
        total: matching.length,
      };
    },

    get: async (id: string) => readAll().find((card) => card.id === id) ?? null,

    create: async (name: string) => {
      const created = createDefaultCard(name, nowIso(), nextCardId());
      writeRaw([...readAll(), created]);
      return created;
    },

    save: async (card: ProductCard) => {
      const cards = readAll();
      const exists = cards.some((candidate) => candidate.id === card.id);
      const next = exists
        ? cards.map((candidate) => (candidate.id === card.id ? card : candidate))
        : [...cards, card];
      writeRaw(next);
      return card;
    },

    remove: async (id: string) => {
      writeRaw(readAll().filter((card) => card.id !== id));
    },
  };
};
